// AppRenderer.tsx

import { AppInsightsContext, ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { lazyRetry } from "helpers/Utils";
import { createBrowserHistory } from "history";
import React, { Suspense } from "react";
import { createRoot, type Root } from "react-dom/client";
import ApolloContextProvider from "./auth/apolloContextProvider";
import CurrentUserProvider from "./auth/CurrentUserProvider";
import { AegisIntrospectionProvider } from "./auth/introspectionProvider";
import Auth0Provider from "./auth/react-auth0-spa";
import ReactTenantProvider from "./auth/reactTenantProvider";
import AegisBrowserRouter from "./components/routing/AegisBrowserRouter";
import reportWebVitals from "./reportWebVitals";
import { unregister } from "./serviceWorker";
import SignalRContextProvider from "./signalR";

const App = React.lazy(() => lazyRetry(() => import("./App")));

// ---- SAFE ENV ACCESS --------------------------------------------------------

type RuntimeEnv = {
	VITE_APPINSIGHTS_KEY?: string;
	VITE_AUTH0_AUDIENCE?: string;
	VITE_AUTH0_CLIENTID?: string;
	VITE_AUTH0_DOMAIN?: string;
	VITE_AUTH0_SCOPE?: string;
	VITE_PUBLIC_URL?: string;
	VITE_ENVIRONMENT_TYPE?: string;
};

const runtimeEnv: RuntimeEnv = (window as any).__ENV ?? {};

const {
	VITE_APPINSIGHTS_KEY = "NONE",
	VITE_AUTH0_AUDIENCE = "",
	VITE_AUTH0_CLIENTID = "",
	VITE_AUTH0_DOMAIN = "",
	VITE_AUTH0_SCOPE = "openid profile email api offline_access",
	VITE_PUBLIC_URL = ""
	// VITE_ENVIRONMENT_TYPE = ""
} = runtimeEnv;

// console.log(
// 	`Envs: APPINSIGHTS_KEY=${VITE_APPINSIGHTS_KEY} AUTH0_AUDIENCE=${VITE_AUTH0_AUDIENCE} AUTH0_CLIENTID=${VITE_AUTH0_CLIENTID} AUTH0_DOMAIN=${VITE_AUTH0_DOMAIN} AUTH0_SCOPE=${VITE_AUTH0_SCOPE} PUBLIC_URL=${VITE_PUBLIC_URL} ENVIRONMENT_TYPE=${VITE_ENVIRONMENT_TYPE}`
// );

// ---- HELPERS / SINGLETONS ---------------------------------------------------

const produceRedirectUrl = (origin: string, publicUrl: string): string => {
	publicUrl = publicUrl ?? "";

	if (!publicUrl.startsWith("/")) {
		publicUrl = `/${publicUrl}`;
	}

	if (!publicUrl.endsWith("/")) {
		publicUrl = `${publicUrl}/`;
	}

	return `${origin}${publicUrl}`;
};

const history = createBrowserHistory();

// Dev-only Apollo diagnostics
if (import.meta.env.DEV) {
	import("@apollo/client/dev")
		.then(({ loadDevMessages, loadErrorMessages }) => {
			loadDevMessages();
			loadErrorMessages();
		})
		.catch(() => {
			/* ignore in prod build where /dev is not published */
		});
}

// App Insights
const reactPlugin = new ReactPlugin();

if (VITE_APPINSIGHTS_KEY !== "NONE" && VITE_APPINSIGHTS_KEY !== "") {
	const appInsights = new ApplicationInsights({
		config: {
			connectionString: VITE_APPINSIGHTS_KEY,
			enableAutoRouteTracking: true,
			extensions: [reactPlugin],
			extensionConfig: {
				[reactPlugin.identifier]: {
					history
				}
			}
		}
	});

	appInsights.config;
	appInsights.loadAppInsights();
}

// Single root instance (React 19 style)
let root: Root | null = null;

function getRoot(): Root {
	if (!root) {
		const container = document.getElementById("root");
		if (!container) {
			throw new Error('Root element "#root" not found');
		}
		root = createRoot(container);
	}
	return root;
}

// ---- EXPORTED RENDER FUNCTION ----------------------------------------------

const AppRenderer = (): void => {
	// console.log('AppRenderer invoked, React version:', React.version);

	const root = getRoot();

	root.render(
		<AegisBrowserRouter history={history}>
			<AppInsightsContext.Provider value={reactPlugin}>
				<Auth0Provider
					domain={VITE_AUTH0_DOMAIN}
					clientId={VITE_AUTH0_CLIENTID}
					useRefreshTokens
					useRefreshTokensFallback
					authorizationParams={{
						redirect_uri: produceRedirectUrl(window.location.origin, VITE_PUBLIC_URL),
						audience: VITE_AUTH0_AUDIENCE,
						scope: VITE_AUTH0_SCOPE
					}}
					cacheLocation="localstorage">
					<ApolloContextProvider>
						<SignalRContextProvider>
							<ReactTenantProvider>
								<AegisIntrospectionProvider>
									<CurrentUserProvider>
										{/* No AegisThemeProvider here — it lives in App.tsx
                        where useThemeMode() is available */}
										<Suspense fallback={<div className="loading" />}>
											<App />
										</Suspense>
									</CurrentUserProvider>
								</AegisIntrospectionProvider>
							</ReactTenantProvider>
						</SignalRContextProvider>
					</ApolloContextProvider>
				</Auth0Provider>
			</AppInsightsContext.Provider>
		</AegisBrowserRouter>
	);

	unregister();
	reportWebVitals();
};

export default AppRenderer;